import { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'
import { FormSection } from 'features/libra/sections/sectionTypes'
import {
  calculateRequiredCoverage,
  getCofModifier,
  getEffectiveStrategyType,
  getEffectiveZoneArea,
  niiPathReaches,
} from 'features/libra/stepComponents/zoneAnalysis/niiStrategy'
import {
  type ZoneCoverageInputs,
  type ZoneParams,
} from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import omit from 'lodash/omit'
import { z } from 'zod'

const INSPECTION_RECOMMENDATIONS_FIELD_ID = 'inspection_recommendations'

export function getInspectionRecommendationsSection(
  sections: readonly FormSection[]
): FormSection | undefined {
  return sections.find(
    (section) =>
      section.agent !== undefined &&
      section.fields.includes(INSPECTION_RECOMMENDATIONS_FIELD_ID)
  )
}

const OMITTED_ZONE_KEYS = [
  'ai_prefill',
  'ai_initial_bounds',
  'ai_meta',
  'drawing_bounds',
  'bounds',
  'color',
] as const satisfies readonly (keyof ZoneParams)[]

/** Builds the agent request from confirmed zones and current client-resolved inputs. */
export function buildRecommendationInputs(
  zones: readonly ZoneParams[],
  { cof, confidence }: ZoneCoverageInputs
) {
  const cofModifier = getCofModifier(cof.level)
  return {
    zones: zones
      .filter((zone) => !zone.ai_suggested)
      .map((zone) => {
        const strategy = zone.nii_strategy
        const reached = niiPathReaches(strategy)
        return {
          ...omit(zone, OMITTED_ZONE_KEYS),
          // Inactive saved answers stay in the editor, not the request.
          nii_strategy: strategy && {
            strategy_type_effective: getEffectiveStrategyType(strategy),
            degradation_likely: strategy.degradation_likely,
            degradation_likely_justification:
              strategy.degradation_likely_justification,
            ...(reached.predictable && {
              degradation_predictable: strategy.degradation_predictable,
              degradation_predictable_justification:
                strategy.degradation_predictable_justification,
            }),
            ...(reached.severity && {
              degradation_severity: strategy.degradation_severity,
              degradation_severity_justification:
                strategy.degradation_severity_justification,
            }),
          },
          coverage_calculation: calculateRequiredCoverage(
            zone,
            cofModifier,
            confidence
          ),
          effective_area_sq_m: getEffectiveZoneArea(zone),
        }
      }),
  }
}

export const zoneRecommendationSchema = z.object({
  zone_id: z.string(),
  zone_title: z.string().nullish(),
  recommendation_text: z.string().nullish(),
})

export type ZoneRecommendation = z.infer<typeof zoneRecommendationSchema>

export interface InspectionRecommendations {
  perZone: ZoneRecommendation[]
  assetLevel: string[]
}

export interface ParsedInspectionRecommendations extends InspectionRecommendations {
  state: 'ready' | 'empty' | 'invalid'
}

/** Parses an agent response without discarding valid rows beside malformed ones. */
export function parseInspectionRecommendations(
  fieldValues: FieldValues
): ParsedInspectionRecommendations {
  const raw = fieldValues.inspection_recommendations?.value
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { perZone: [], assetLevel: [], state: 'invalid' }
  }

  const payload = raw as { per_zone?: unknown; asset_level?: unknown }
  if (
    (payload.per_zone !== undefined && !Array.isArray(payload.per_zone)) ||
    (payload.asset_level !== undefined && !Array.isArray(payload.asset_level))
  ) {
    return { perZone: [], assetLevel: [], state: 'invalid' }
  }

  const rawPerZone = Array.isArray(payload.per_zone) ? payload.per_zone : []
  const rawAssetLevel = Array.isArray(payload.asset_level)
    ? payload.asset_level
    : []
  const perZone = rawPerZone.flatMap((item) => {
    const parsed = zoneRecommendationSchema.safeParse(item)
    return parsed.success ? [parsed.data] : []
  })
  const assetLevel = rawAssetLevel
    .filter((s): s is string => typeof s === 'string')
    .map((s) => s.trim())
    .filter((s) => s.length > 0)

  const hasResult = perZone.length > 0 || assetLevel.length > 0
  const rejectedEntirePayload =
    (rawPerZone.length > 0 && perZone.length === 0) ||
    (rawAssetLevel.length > 0 && assetLevel.length === 0)

  return {
    perZone,
    assetLevel,
    state: hasResult ? 'ready' : rejectedEntirePayload ? 'invalid' : 'empty',
  }
}

/** Converts per-zone and asset-level recommendations into Closeout text lines. */
export function flattenRecommendations(
  { perZone, assetLevel }: InspectionRecommendations,
  zones: readonly ZoneParams[]
): string[] {
  const titleById = new Map(zones.map((zone) => [zone.id, zone.title]))

  const zoneLines = perZone.flatMap((rec) => {
    const text = rec.recommendation_text?.trim()
    if (!text) return []
    const label = (
      rec.zone_title?.trim() ||
      titleById.get(rec.zone_id) ||
      rec.zone_id
    ).trim()
    if (!label || text.toLowerCase().startsWith(label.toLowerCase())) {
      return [text]
    }
    return [`${label}: ${text}`]
  })

  return [...zoneLines, ...assetLevel]
}
